document.documentElement.classList.remove('no-js');

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initLoader() {
    const loader = document.querySelector('[data-bm-loader]');
    if (!loader) return;
    const hide = () => {
      loader.classList.add('is-hidden');
      document.body.classList.remove('bm-no-scroll');
      window.setTimeout(() => loader.remove(), 800);
    };
    document.body.classList.add('bm-no-scroll');
    if (document.readyState === 'complete') window.setTimeout(hide, 420);
    else window.addEventListener('load', () => window.setTimeout(hide, 420), { once: true });
    window.setTimeout(hide, 2600);
  }

  function initMenu() {
    const menuButton = document.querySelector('[data-menu-button]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!menuButton || !nav || menuButton.dataset.bmInitialized) return;
    menuButton.dataset.bmInitialized = 'true';
    menuButton.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      menuButton.setAttribute('aria-expanded', String(open));
    });
    nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
      nav.classList.remove('is-open');
      menuButton.setAttribute('aria-expanded', 'false');
    }));
  }

  function initReveal() {
    const items = [...document.querySelectorAll('.bm-reveal')];
    if (!items.length || reducedMotion) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    items.forEach((item) => observer.observe(item));
  }

  function initCursor() {
    const cursor = document.querySelector('[data-bm-cursor]');
    if (!cursor || reducedMotion || window.matchMedia('(pointer: coarse)').matches) {
      cursor?.remove();
      return;
    }
    if (cursor.dataset.bmInitialized) return;
    cursor.dataset.bmInitialized = 'true';
    let x = -100, y = -100, tx = x, ty = y;
    window.addEventListener('pointermove', (event) => { tx = event.clientX; ty = event.clientY; });
    const loop = () => {
      x += (tx - x) * .18; y += (ty - y) * .18;
      cursor.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      requestAnimationFrame(loop);
    };
    loop();
    document.querySelectorAll('a,button,.bm-project,.bm-step,.bm-media-frame').forEach((element) => {
      element.addEventListener('mouseenter', () => cursor.classList.add('is-active'));
      element.addEventListener('mouseleave', () => cursor.classList.remove('is-active'));
    });
  }

  function initTilt() {
    if (reducedMotion || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('[data-tilt]').forEach((card) => {
      if (card.dataset.bmTiltInitialized) return;
      card.dataset.bmTiltInitialized = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const rx = ((event.clientY - rect.top) / rect.height - .5) * -7;
        const ry = ((event.clientX - rect.left) / rect.width - .5) * 7;
        card.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  }

  function initParallax() {
    if (reducedMotion) return;
    const elements = [...document.querySelectorAll('[data-parallax]')];
    if (!elements.length || window.__bmParallaxInitialized) return;
    window.__bmParallaxInitialized = true;
    let ticking = false;
    const update = () => {
      const viewport = window.innerHeight;
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const progress = (viewport - rect.top) / (viewport + rect.height);
        const speed = Number(el.dataset.parallax || .12);
        el.style.setProperty('--bm-parallax', `${(progress - .5) * speed * 180}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  function initCarousel() {
    document.querySelectorAll('[data-bm-carousel]').forEach((root) => {
      if (root.dataset.bmCarouselInitialized) return;
      root.dataset.bmCarouselInitialized = 'true';
      const cards = [...root.querySelectorAll('.bm-quote')];
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      if (!cards.length) return;
      let active = 0;
      let timer;
      const render = () => {
        cards.forEach((card, index) => {
          card.classList.remove('is-prev', 'is-active', 'is-next');
          const previous = (active - 1 + cards.length) % cards.length;
          const following = (active + 1) % cards.length;
          if (index === active) card.classList.add('is-active');
          else if (index === previous) card.classList.add('is-prev');
          else if (index === following) card.classList.add('is-next');
        });
      };
      const move = (direction) => {
        active = (active + direction + cards.length) % cards.length;
        render();
      };
      const autoplay = () => {
        window.clearInterval(timer);
        if (!reducedMotion) timer = window.setInterval(() => move(1), 4200);
      };
      prev?.addEventListener('click', () => { move(-1); autoplay(); });
      next?.addEventListener('click', () => { move(1); autoplay(); });
      root.addEventListener('mouseenter', () => window.clearInterval(timer));
      root.addEventListener('mouseleave', autoplay);
      render(); autoplay();
    });
  }

  function initMagneticButtons() {
    if (reducedMotion || window.matchMedia('(pointer: coarse)').matches) return;
    document.querySelectorAll('.bm-button').forEach((button) => {
      if (button.dataset.bmMagneticInitialized) return;
      button.dataset.bmMagneticInitialized = 'true';
      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();
        const x = (event.clientX - rect.left - rect.width / 2) * .14;
        const y = (event.clientY - rect.top - rect.height / 2) * .18;
        button.style.transform = `translate(${x}px, ${y}px)`;
      });
      button.addEventListener('pointerleave', () => { button.style.transform = ''; });
    });
  }

  function initBrightMedia() {
    initLoader();
    initMenu();
    initReveal();
    initCursor();
    initTilt();
    initParallax();
    initCarousel();
    initMagneticButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBrightMedia, { once: true });
  } else {
    initBrightMedia();
  }
  document.addEventListener('shopify:section:load', initBrightMedia);
})();
